import {
  WalletAdapterNetwork,
  WalletReadyState,
} from '@solana/wallet-adapter-base';
import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom';
import { SolflareWalletAdapter } from '@solana/wallet-adapter-solflare';
import {
  isWalletAdapterCompatibleWallet,
  StandardWalletAdapter,
} from '@solana/wallet-standard-wallet-adapter-base';
import {
  createDefaultAuthorizationCache,
  createDefaultChainSelector,
  createDefaultWalletNotFoundHandler,
  registerMwa,
} from '@solana-mobile/wallet-standard-mobile';
import { DEPRECATED_getWallets } from '@wallet-standard/app';

const AUTO_CONNECT_KEY = 'wallet_autoconnect';
const PROVIDER_KEY = 'wallet_provider';
const WALLET_PRECEDENCE = [
  'Jupiter Mobile',
  'Mobile Wallet Adapter',
  'Phantom',
  'Solflare',
  'Backpack',
];

let activeAdapter = null;
let legacyAdapters = null;
let walletRegistry = null;
let mobileWalletRegistered = false;
let walletRegistryListenersBound = false;
let allAdapters = [];

const standardAdaptersByWallet = new Map();
const boundAdapters = new WeakSet();

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function isMobileDevice() {
  return isBrowser() && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

function normalizePublicKey(publicKey) {
  if (!publicKey) return '';
  return typeof publicKey === 'string' ? publicKey : publicKey.toString();
}

function getAdapterAddress(adapter) {
  return normalizePublicKey(adapter?.publicKey);
}

function getAdapterName(adapter) {
  return adapter?.name ? String(adapter.name) : '';
}

function forgetStoredConnection() {
  localStorage.removeItem(AUTO_CONNECT_KEY);
  localStorage.removeItem(PROVIDER_KEY);
}

function getAppIdentity() {
  const origin = isBrowser() ? window.location.origin : 'https://currentseas.fun';

  return {
    name: 'CurrentSeas',
    uri: origin,
  };
}

function ensureMobileWalletAdapter() {
  if (!isBrowser() || mobileWalletRegistered) return;

  try {
    registerMwa({
      appIdentity: getAppIdentity(),
      authorizationCache: createDefaultAuthorizationCache(),
      chains: ['solana:mainnet', 'solana:devnet'],
      chainSelector: createDefaultChainSelector(),
      onWalletNotFound: createDefaultWalletNotFoundHandler(),
    });
    mobileWalletRegistered = true;
  } catch (error) {
    console.warn('Mobile Wallet Adapter registration failed:', error);
  }
}

function getWalletRegistry() {
  if (!walletRegistry) {
    walletRegistry = DEPRECATED_getWallets();
  }
  return walletRegistry;
}

function getLegacyAdapters() {
  if (!legacyAdapters) {
    legacyAdapters = [
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter({ network: WalletAdapterNetwork.Mainnet }),
    ];
  }
  return legacyAdapters;
}

function syncStandardAdapters() {
  if (!isBrowser()) return [];

  const registry = getWalletRegistry();
  const wallets = registry.get().filter(isWalletAdapterCompatibleWallet);
  const currentWallets = new Set(wallets);

  for (const [wallet, adapter] of standardAdaptersByWallet.entries()) {
    if (!currentWallets.has(wallet)) {
      if (adapter !== activeAdapter && typeof adapter.destroy === 'function') {
        adapter.destroy();
      }
      standardAdaptersByWallet.delete(wallet);
    }
  }

  return wallets.map((wallet) => {
    if (!standardAdaptersByWallet.has(wallet)) {
      standardAdaptersByWallet.set(wallet, new StandardWalletAdapter({ wallet }));
    }
    return standardAdaptersByWallet.get(wallet);
  });
}

function readyStateRank(readyState) {
  switch (readyState) {
    case WalletReadyState.Installed:
      return 0;
    case WalletReadyState.Loadable:
      return 1;
    case WalletReadyState.NotDetected:
      return 2;
    case WalletReadyState.Unsupported:
      return 3;
    default:
      return 4;
  }
}

function precedenceRank(adapter) {
  const index = WALLET_PRECEDENCE.indexOf(getAdapterName(adapter));
  return index === -1 ? WALLET_PRECEDENCE.length : index;
}

function sortAdapters(adapters) {
  return [...adapters].sort((a, b) => {
    if (a === activeAdapter) return -1;
    if (b === activeAdapter) return 1;

    const readyDiff = readyStateRank(a.readyState) - readyStateRank(b.readyState);
    if (readyDiff !== 0) return readyDiff;

    const precedenceDiff = precedenceRank(a) - precedenceRank(b);
    if (precedenceDiff !== 0) return precedenceDiff;

    return getAdapterName(a).localeCompare(getAdapterName(b));
  });
}

function adapterDescriptor(adapter) {
  return {
    name: getAdapterName(adapter),
    icon: adapter.icon || '',
    url: adapter.url || '',
    readyState: adapter.readyState || WalletReadyState.NotDetected,
    connected: adapter === activeAdapter && Boolean(adapter.publicKey),
    installed: adapter.readyState === WalletReadyState.Installed,
    loadable: adapter.readyState === WalletReadyState.Loadable,
    standard: Boolean(adapter.standard),
  };
}

function refreshAdapters(store) {
  ensureMobileWalletAdapter();

  const standardAdapters = syncStandardAdapters();
  const standardNames = new Set(standardAdapters.map(getAdapterName));
  const legacyFallbacks = getLegacyAdapters().filter((adapter) => !standardNames.has(getAdapterName(adapter)));

  allAdapters = sortAdapters([...standardAdapters, ...legacyFallbacks]);
  allAdapters.forEach((adapter) => bindAdapterEvents(store, adapter));

  if (store) {
    store.wallets = allAdapters.map(adapterDescriptor);
  }

  return allAdapters;
}

function bindWalletRegistryEvents(store) {
  if (!isBrowser() || walletRegistryListenersBound) return;

  const registry = getWalletRegistry();
  registry.on('register', () => refreshAdapters(store));
  registry.on('unregister', () => refreshAdapters(store));
  walletRegistryListenersBound = true;
}

function setConnectedState(store, adapter, publicKey) {
  const address = normalizePublicKey(publicKey || adapter.publicKey);
  if (!address) return;

  activeAdapter = adapter;
  store.address = address;
  store.walletName = getAdapterName(adapter);
  store.isConnected = true;
  store.connectionError = '';

  localStorage.setItem(AUTO_CONNECT_KEY, 'true');
  localStorage.setItem(PROVIDER_KEY, store.walletName);
  refreshAdapters(store);
  maybeRefreshRealityCoins(address);

  window.dispatchEvent(new CustomEvent('wallet-connected', {
    detail: { address, walletName: store.walletName },
  }));
}

function clearConnectedState(store, adapter) {
  if (adapter && activeAdapter && adapter !== activeAdapter) return;

  activeAdapter = null;
  store.address = '';
  store.walletName = '';
  store.isConnected = false;
  store.connectionError = '';
  forgetStoredConnection();
  refreshAdapters(store);

  window.dispatchEvent(new CustomEvent('wallet-disconnected'));
}

function bindAdapterEvents(store, adapter) {
  if (!adapter || boundAdapters.has(adapter) || typeof adapter.on !== 'function') return;
  boundAdapters.add(adapter);

  adapter.on('connect', (publicKey) => {
    setConnectedState(store, adapter, publicKey);
  });

  adapter.on('disconnect', () => {
    clearConnectedState(store, adapter);
  });

  adapter.on('readyStateChange', () => {
    refreshAdapters(store);
  });

  adapter.on('error', (error) => {
    const message = normalizeWalletError(error, 'Wallet error');
    store.connectionError = message;
    window.dispatchEvent(new CustomEvent('wallet-error', {
      detail: { message, walletName: getAdapterName(adapter) },
    }));
  });
}

function pickAdapter(store, preferredName = '') {
  const adapters = refreshAdapters(store);
  const storedName = localStorage.getItem(PROVIDER_KEY) || '';
  const targetName = preferredName || storedName;

  if (targetName) {
    const target = adapters.find((adapter) => getAdapterName(adapter) === targetName);
    if (target) return target;
  }

  return adapters.find((adapter) => adapter.connected || adapter.publicKey)
    || adapters.find((adapter) => adapter.readyState === WalletReadyState.Installed)
    || adapters.find((adapter) => adapter.readyState === WalletReadyState.Loadable)
    || adapters.find((adapter) => adapter.readyState !== WalletReadyState.Unsupported)
    || null;
}

function getAdapterByName(store, name) {
  if (!name) return null;
  return refreshAdapters(store).find((adapter) => getAdapterName(adapter) === name) || null;
}

function normalizeConnectArgs(eventOrName, preferredName = '') {
  if (typeof eventOrName === 'string') {
    return { event: null, preferredName: eventOrName };
  }

  return { event: eventOrName, preferredName };
}

function normalizeWalletError(error, fallback = 'Failed to connect to wallet') {
  const rawMessage = error?.message || String(error || '');

  if (/not ready|not detected|not installed/i.test(rawMessage)) {
    return 'Wallet is not installed or is not available in this browser.';
  }
  if (/unexpected error/i.test(rawMessage)) {
    return 'Wallet connection was interrupted. Unlock your wallet, close any pending wallet popups, and try again.';
  }
  if (/user rejected|rejected the request|cancel/i.test(rawMessage)) {
    return 'Wallet connection was cancelled.';
  }
  if (/WalletAccountError/i.test(rawMessage)) {
    return 'The selected wallet did not return a Solana account.';
  }

  return rawMessage || fallback;
}

async function maybeRefreshRealityCoins(address) {
  const coins = window.Alpine?.store ? window.Alpine.store('realityCoins') : null;
  if (!address || typeof coins?.checkBalance !== 'function') return;

  try {
    await coins.checkBalance();
  } catch {
    // Wallet connection should not fail just because rewards metadata is unavailable.
  }
}

function requireConnectedAdapter(store) {
  const adapter = activeAdapter || getAdapterByName(store, store.walletName);
  if (!store.isConnected || !adapter) {
    throw new Error('Wallet not connected');
  }
  return adapter;
}

export function createWalletStore() {
  const store = {
    address: '',
    walletName: '',
    connectionError: '',
    isConnected: false,
    isConnecting: false,
    wallets: [],

    get availableWallets() {
      return this.wallets.map(({ name }) => name);
    },

    refreshWallets() {
      bindWalletRegistryEvents(this);
      refreshAdapters(this);
      return this.wallets;
    },

    getProvider() {
      if (activeAdapter) return activeAdapter;

      const selected = pickAdapter(this, this.walletName || localStorage.getItem(PROVIDER_KEY) || '');
      activeAdapter = selected || null;
      return activeAdapter;
    },

    async connect(eventOrName, preferredName = '') {
      const args = normalizeConnectArgs(eventOrName, preferredName);
      if (args.event?.preventDefault) args.event.preventDefault();
      if (this.isConnecting) return false;

      this.isConnecting = true;
      this.connectionError = '';

      try {
        const adapter = pickAdapter(this, args.preferredName);

        if (!adapter) {
          throw new Error('No Solana wallet adapter is available');
        }

        if (adapter.readyState === WalletReadyState.Unsupported) {
          throw new Error(`${getAdapterName(adapter)} is not supported in this browser`);
        }

        if (adapter.readyState === WalletReadyState.NotDetected && !isMobileDevice()) {
          throw new Error(`${getAdapterName(adapter)} is not installed`);
        }

        activeAdapter = adapter;
        this.walletName = getAdapterName(adapter);
        bindAdapterEvents(this, adapter);

        if (!adapter.connected && !adapter.publicKey) {
          await adapter.connect();
        }

        const address = getAdapterAddress(adapter);
        if (!address) {
          throw new Error('No public key received from wallet');
        }

        setConnectedState(this, adapter, adapter.publicKey);
        return true;
      } catch (error) {
        const message = normalizeWalletError(error);
        activeAdapter = null;
        this.address = '';
        this.walletName = '';
        this.isConnected = false;
        this.connectionError = message;
        forgetStoredConnection();
        refreshAdapters(this);
        return false;
      } finally {
        this.isConnecting = false;
      }
    },

    async disconnect() {
      const adapter = activeAdapter || getAdapterByName(this, this.walletName);

      try {
        if (adapter && (adapter.connected || adapter.publicKey) && typeof adapter.disconnect === 'function') {
          await adapter.disconnect();
        }
      } catch (error) {
        console.error('Error disconnecting wallet:', error);
      }

      clearConnectedState(this, adapter);
      forgetStoredConnection();
    },

    async tryAutoConnect() {
      if (localStorage.getItem(AUTO_CONNECT_KEY) !== 'true') {
        return false;
      }

      const adapter = pickAdapter(this, localStorage.getItem(PROVIDER_KEY) || '');
      if (!adapter || adapter.readyState === WalletReadyState.Unsupported || adapter.readyState === WalletReadyState.NotDetected) {
        localStorage.removeItem(AUTO_CONNECT_KEY);
        return false;
      }

      try {
        activeAdapter = adapter;
        this.walletName = getAdapterName(adapter);
        bindAdapterEvents(this, adapter);

        if (!adapter.connected && !adapter.publicKey) {
          if (typeof adapter.autoConnect === 'function') {
            await adapter.autoConnect();
          } else {
            await adapter.connect();
          }
        }

        const address = getAdapterAddress(adapter);
        if (!address) return false;

        setConnectedState(this, adapter, adapter.publicKey);
        return true;
      } catch {
        activeAdapter = null;
        this.address = '';
        this.walletName = '';
        this.isConnected = false;
        forgetStoredConnection();
        refreshAdapters(this);
        return false;
      }
    },

    async signTransaction(transaction) {
      const adapter = requireConnectedAdapter(this);
      if (typeof adapter.signTransaction !== 'function') {
        throw new Error('Connected wallet cannot sign transactions');
      }
      return adapter.signTransaction(transaction);
    },

    async signAllTransactions(transactions) {
      const adapter = requireConnectedAdapter(this);
      if (typeof adapter.signAllTransactions !== 'function') {
        throw new Error('Connected wallet cannot sign multiple transactions');
      }
      return adapter.signAllTransactions(transactions);
    },

    async signMessage(message) {
      const adapter = requireConnectedAdapter(this);
      if (typeof adapter.signMessage !== 'function') {
        throw new Error('Connected wallet cannot sign messages');
      }
      return adapter.signMessage(message);
    },

    async sendTransaction(transaction, connection, options = {}) {
      const adapter = requireConnectedAdapter(this);
      if (!connection) {
        throw new Error('A Solana connection is required to send transactions');
      }
      if (typeof adapter.sendTransaction !== 'function') {
        throw new Error('Connected wallet cannot send transactions');
      }
      return adapter.sendTransaction(transaction, connection, options);
    },
  };

  store.refreshWallets();
  return store;
}
